"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const capabilities = [{
  id: "cap-1",
  title: "Amusement Park Planning and Design",
  description: "Comprehensive master planning, conceptual design, and schematic development for world-class amusement parks and cultural destinations. We ensure thematic consistency, operational viability, and adherence to international safety and design standards from the very first sketch."
}, {
  id: "cap-2",
  title: "Engineering and Construction",
  description: "Full-scale engineering services encompassing civil, structural, mechanical, electrical, and plumbing (MEP) design. Our rigorous construction management approach guarantees that complex tourism infrastructure is built to exact specifications, on time, and within budget."
}, {
  id: "cap-3",
  title: "Procurement of Theme Park Rides",
  description: "Strategic sourcing and procurement of high-end amusement rides, show equipment, and specialized thematic materials. We leverage our global network of top-tier manufacturers to ensure unparalleled safety, reliability, and guest experience."
}, {
  id: "cap-4",
  title: "Tourism Destination Operations",
  description: "End-to-end operational management, bridging the gap between construction completion and grand opening. Our services include pre-opening SOP development, staff training, facility management, and data-driven yield optimization for sustained profitability."
}];
const capabilitySpecs = [{
  capabilityId: "cap-1",
  label: "Key Deliverables",
  value: "Master Plan, Concept Art, Schematic Design"
}, {
  capabilityId: "cap-1",
  label: "Design Standards",
  value: "International Theme Park Standards"
}, {
  capabilityId: "cap-1",
  label: "Primary Tools",
  value: "AutoCAD, Revit, 3ds Max, Rhino"
}, {
  capabilityId: "cap-2",
  label: "Engineering Scope",
  value: "Civil, Structural, MEP, Ride Foundation"
}, {
  capabilityId: "cap-2",
  label: "Compliance",
  value: "Local & International Building Codes"
}, {
  capabilityId: "cap-2",
  label: "Execution Model",
  value: "Turnkey Construction Management"
}, {
  capabilityId: "cap-3",
  label: "Vendor Network",
  value: "Global Tier-1 Ride Manufacturers"
}, {
  capabilityId: "cap-3",
  label: "Quality Assurance",
  value: "TUV / ASTM Certification Standards"
}, {
  capabilityId: "cap-3",
  label: "Logistics",
  value: "End-to-end International Supply Chain"
}, {
  capabilityId: "cap-4",
  label: "Pre-opening",
  value: "SOP Development, Comprehensive Staff Training"
}, {
  capabilityId: "cap-4",
  label: "Core Operations",
  value: "Facility Management, Ticketing, F&B Integration"
}, {
  capabilityId: "cap-4",
  label: "Performance",
  value: "Data-driven Yield & Revenue Management"
}];
export default function OurCoreCapabilities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-core-capabilities" className="bg-background py-24 lg:py-32 overflow-hidden">
      <div ref={ref} className={`mx-auto max-w-5xl px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mb-16 max-w-2xl">
          <h2 className="text-3xl font-light tracking-tight text-foreground sm:text-4xl mb-6">
            Our Core Capabilities
          </h2>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            Deep dive into AOTO's integrated tourism development model, from initial planning to destination operations. We deliver end-to-end EPCO solutions tailored for large-scale international projects.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full border-t border-border">
          {capabilities.map((cap, i) => <AccordionItem key={cap.id} value={cap.id} data-index={i} className="border-border">
              <AccordionTrigger className="py-6 text-lg sm:text-xl font-medium text-foreground hover:text-primary hover:no-underline transition-colors">
                {cap.title}
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 pt-4 pb-10">
                  <div className="lg:col-span-5">
                    <p className="text-[15px] leading-relaxed text-muted-foreground">
                      {cap.description}
                    </p>
                  </div>
                  <div className="lg:col-span-7">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="w-1/3 text-xs font-semibold uppercase tracking-wider text-muted-foreground h-auto pb-4">
                            Technical Spec
                          </TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground h-auto pb-4">
                            Detail
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {capabilitySpecs.filter(spec => spec.capabilityId === cap.id).map((spec, j) => <TableRow key={j} data-index={j} className="border-border hover:bg-muted/30 transition-colors">
                              <TableCell className="py-4 align-top text-[15px] font-medium text-foreground">
                                {spec.label}
                              </TableCell>
                              <TableCell className="py-4 align-top text-[15px] text-muted-foreground">
                                {spec.value}
                              </TableCell>
                            </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}